import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import { fetchAuthWrapper } from '@starjack/api';

const baseUrl = urlBuilder('order', 'reporting/reports');

export type ProductEnvironment =
    | 'PORTAL'
    | 'INTEGRATION'
    | 'B2B'
    | 'HOSPITALITY';

/** One channel/payment combination inside a sales row. */
export type InsuranceChannel = {
    environment: ProductEnvironment;
    /** True when the operator collected the money itself rather than starjack. */
    externalPayment: boolean;
    soldCount: number;
    grossPremium: number;
    confirmedPremium: number;
};

export type InsuranceRow = {
    liftOperatorId: string;
    liftOperatorName: string | null;
    tariffIdentifier: string;
    tariffName: string | null;
    soldCount: number;
    grossPremium: number;
    /** Confirmed by the insurer — the invoice figure. Does not shrink on later cancellations. */
    confirmedCount: number;
    confirmedPremium: number;
    /**
     * Paid but never confirmed by the gateway. A clearing item, never an invoice item.
     *
     * Overlaps the status split: cancelling does not require a prior confirmation, so a
     * policy can be both unconfirmed and cancelled. Never add this into the status sum.
     */
    unconfirmedCount: number;
    unconfirmedPremium: number;
    /** Confirmed and still in force today. Control figure only. */
    activeCount: number;
    activePremium: number;
    /** Neither confirmed nor terminated nor blocked — still waiting on the gateway. */
    openCount: number;
    openPremium: number;
    canceledCount: number;
    canceledPremium: number;
    revokedCount: number;
    revokedPremium: number;
    permissionBlockedCount: number;
    permissionBlockedPremium: number;
    ticketsSold: number;
    /** Null when the operator sold no tickets at all — that is not a rate of zero. */
    attachRatePct: number | null;
    channels: InsuranceChannel[];
};

/** Cancellations and revocations that happened in the period, whenever they were sold. */
export type InsuranceTerminationRow = {
    liftOperatorId: string;
    liftOperatorName: string | null;
    tariffIdentifier: string;
    tariffName: string | null;
    canceledCount: number;
    canceledPremium: number;
    revokedCount: number;
    revokedPremium: number;
    /** Credits actually granted. Lower than the premium when a termination refunded nothing. */
    refundedAmount: number;
};

export type InsuranceOperatorWithoutSales = {
    liftOperatorId: string;
    liftOperatorName: string | null;
    ticketsSold: number;
};

export type InsuranceTotals = {
    soldCount: number;
    grossPremium: number;
    confirmedPremium: number;
    unconfirmedPremium: number;
    activePremium: number;
    openPremium: number;
    terminatedPremium: number;
    permissionBlockedPremium: number;
};

/**
 * Jackson serialises LocalDate as [year, month, day], not as an ISO string — verified
 * against the running service. Typed as the tuple so nobody formats it as text.
 */
export type LocalDateTuple = [number, number, number];

export type InsuranceReport = {
    from: LocalDateTuple;
    to: LocalDateTuple;
    environments: ProductEnvironment[];
    rows: InsuranceRow[];
    terminationsInPeriod: InsuranceTerminationRow[];
    operatorsWithoutInsurance: InsuranceOperatorWithoutSales[];
    totals: InsuranceTotals;
};

export type InsuranceQuery = {
    from: string;
    to: string;
    environments?: ProductEnvironment[];
};

const searchOf = ({ from, to, environments }: InsuranceQuery) => {
    const search = new URLSearchParams({ from, to });
    // Repeated key, not a comma list — Spring binds one Set entry per occurrence.
    environments?.forEach((e) => search.append('environments', e));
    return search.toString();
};

export const insuranceReportApi = baseApiAuthenticated.injectEndpoints({
    endpoints: (builder) => ({
        getInsuranceReport: builder.query<InsuranceReport, InsuranceQuery>({
            query: (params) => `${baseUrl}/insurance?${searchOf(params)}`,
        }),
    }),
});

export const { useGetInsuranceReportQuery } = insuranceReportApi;

export const getInsuranceExcel = async (
    params: InsuranceQuery
): Promise<Blob | null> => {
    // A dropped connection must surface as a failed download, not as an
    // unhandled rejection that leaves the button stuck in its loading state.
    try {
        const response = await fetchAuthWrapper(
            `${baseUrl}/insurance.xlsx?${searchOf(params)}`,
            {
                headers: {
                    Accept: '*',
                    'Content-Type':
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
                method: 'GET',
            }
        );

        if (!response.ok) {
            return null;
        }

        return await response.blob();
    } catch {
        return null;
    }
};
